import * as React from 'react';
import * as Popover from '@radix-ui/react-popover';
import * as Dialog from '@radix-ui/react-dialog';

import type { RoomsParticipantDistribution as RoomsParticipantDistributionType } from '@/domain/shared/types';

import { BaseInput, Button, Icon, Typography } from '@sunwebgroup/ui-components';
import { useIntl } from '@/intl';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints';
import { createPricesFilterClickEvent } from '@/domain/prices/analytics';
import { usePrices } from '@/domain/prices/prices/hooks';
import { formatParticipantDistributionToGA4Result } from '@/scopes/search/search/domain/analytics/mappers';
import { usePricesStore } from '@/infrastructure/prices/usePricesStore';
import * as ParticipantsFilter from '@/scopes/common/components/Filters/Participants';
import { ChildInfo, GroupLink } from '@/scopes/common/components/Filters/Participants/ParticipantsBanner';
import { useParticipantsFilter } from '@/scopes/common/components/Filters/Participants/hooks';
import { PopoverBody, PopoverContent } from '@/scopes/common/components/Filters/Shared';
import * as DialogPrimitive from '@/scopes/search/components/QuickSearchBar/DialogPrimitive';
import { Input } from './BaseFilter/Input';
import { useAccommodationAvailability } from '@/domain/prices/availability/hooks';

export function ParticipantsDropdownFilter() {
  const intl = useIntl();
  const { isTablet } = useBreakpoints();
  const [isOpen, setIsOpen] = React.useState(false);
  const { setFilterSelection, filterSelection } = usePricesStore();
  const { sendEvent } = useAnalytics();
  const { isFetching } = usePrices();
  const { isFlightOnly } = useAccommodationAvailability();
  const { handleConfirm, getFormattedText, showParticipants, showChildren, roomsParticipantDistribution, setRooms } =
    useParticipantsFilter({
      isFetching,
      setSelection: setParticipantsSelection,
    });

  React.useEffect(() => {
    if (!filterSelection.Participants) return;
    setRooms(filterSelection.Participants);
  }, [filterSelection, setRooms]);

  function setParticipantsSelection(selection: RoomsParticipantDistributionType) {
    setFilterSelection('Participants', selection);
  }

  function toggle() {
    if (!isFetching) {
      handleConfirm();

      if (isOpen) {
        //the filter is closing
        sendEvent(
          createPricesFilterClickEvent({
            element: 'participants selector',
            type: 'partyComposition',
            result: formatParticipantDistributionToGA4Result(roomsParticipantDistribution).distribution,
          })
        );
      }
      setIsOpen(!isOpen);
    }
  }

  return (
    <>
      {isTablet && (
        <Popover.Root open={isOpen} onOpenChange={toggle}>
          <Popover.Trigger data-qa="participants-filter-input">
            <Input
              label={intl.formatMessage({ id: 'prices_filters_participants_label' })}
              disabled={isFetching}
              icon="IconFamily"
            >
              <BaseInput.Content>{getFormattedText().desktop}</BaseInput.Content>
            </Input>
          </Popover.Trigger>
          <Popover.Portal>
            <PopoverContent align="start">
              <PopoverBody>
                {(showChildren || showParticipants) && (
                  <ParticipantsFilter.BannerWrapper>
                    {showChildren && <ChildInfo />}
                    {showParticipants && <GroupLink />}
                  </ParticipantsFilter.BannerWrapper>
                )}
                <ParticipantsFilter.Body isMultiRoom={!isFlightOnly} />
                <ParticipantsFilter.Footer>
                  <ParticipantsFilter.CloseButton
                    $fullSize
                    onClick={toggle}
                    disabled={isFetching}
                    data-qa="participants-close-button"
                  >
                    {intl.formatMessage({
                      id: 'quicksearch_save_button_text',
                    })}
                  </ParticipantsFilter.CloseButton>
                </ParticipantsFilter.Footer>
              </PopoverBody>
            </PopoverContent>
          </Popover.Portal>
        </Popover.Root>
      )}

      {!isTablet && (
        <>
          <Input
            label={intl.formatMessage({ id: 'prices_filters_participants_label' })}
            disabled={isFetching}
            icon="IconFamily"
            toggle={toggle}
          >
            <BaseInput.Content>{getFormattedText().desktop}</BaseInput.Content>
          </Input>
          <Dialog.Root open={isOpen} modal={false}>
            <Dialog.Portal>
              <Dialog.Content asChild>
                <DialogPrimitive.Root>
                  <DialogPrimitive.Header>
                    <DialogPrimitive.HeaderTitle>
                      <Typography $variant="body-lg-bold">
                        {intl.formatMessage({ id: 'prices_filters_participants_label' })}
                      </Typography>
                      <Button onClick={toggle} $hierarchy="tertiary" $withoutColor>
                        <Icon glyph="IconCrossMedium" size="md" />
                      </Button>
                    </DialogPrimitive.HeaderTitle>
                  </DialogPrimitive.Header>
                  {(showChildren || showParticipants) && (
                    <ParticipantsFilter.BannerWrapper>
                      {showChildren && <ChildInfo />}
                      {showParticipants && <GroupLink />}
                    </ParticipantsFilter.BannerWrapper>
                  )}
                  <DialogPrimitive.Body>
                    <ParticipantsFilter.Body isMultiRoom={!isFlightOnly} />
                  </DialogPrimitive.Body>
                  <DialogPrimitive.Footer>
                    <ParticipantsFilter.Footer>
                      <ParticipantsFilter.CloseButton $fullSize onClick={toggle} disabled={isFetching}>
                        {intl.formatMessage({
                          id: 'quicksearch_save_button_text',
                        })}
                      </ParticipantsFilter.CloseButton>
                    </ParticipantsFilter.Footer>
                  </DialogPrimitive.Footer>
                </DialogPrimitive.Root>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        </>
      )}
    </>
  );
}
